import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  DexServiceConfig, 
  WalletConfig, 
  RpcConfig, 
  DexConfig,
  GasSettingMode, 
  NetworkCongestionLevel,
  GasSettingsConfig,
  GasPrices
} from '../types/dex';
import { RootState } from '.';
import { ethers, BigNumber } from 'ethers';

// 队列运行模式
export enum QueueRunMode {
  SERIAL = '串行',  // 串行执行，一个接一个
  PARALLEL = '并行'  // 并行执行，同时执行多个
}

// 队列停止条件
export enum QueueStopCondition {
  RUN_COUNT = '运行次数',  // 运行指定次数后停止
  PRICE_INCREASE = '价格上涨时',  // 价格上涨到指定比例时停止
  PRICE_DECREASE = '价格下跌时'  // 价格下跌到指定比例时停止
}

// 初始状态
export interface DexConfigState {
  config: DexServiceConfig;
  task?: TaskConfig;
  tokens: TokenInfo[];
  swapParams: SwapParams;
}

// 默认配置，当本地存储为空时使用
const defaultConfig: DexServiceConfig = {
  wallets: [],
  rpc: {
    url: 'https://bsc-mainnet.nodereal.io/v1/4b06d775febe481fbc614202d940a7cf',
    name: 'BSC',
    chainId: 56,
    symbol: 'BNB'
  },
  dex: {
    router: {
      address: '0x10ED43C718714eb63d5aA57B78B54704E256024E',
      name: 'Uniswap V2 Router'
    },
    factory: {
      address: '0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73'
    },
  },
  gasSettings: {
    mode: GasSettingMode.AUTO,
    congestionLevel: NetworkCongestionLevel.MEDIUM,
    gasPrice: '5',
    gasLimit: '300000'
  }
};

// 定义代币信息接口
export interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoUrl?: string;
}

// 定义交换配置接口
export interface SwapConfig {
  token1: TokenInfo | null;
  token2: TokenInfo | null;
  slippage: number;
  amountType: string;  // 金额类型：ALL, RANDOM, RANDOM_PERCENT, FIXED, RESERVE
  minAmount?: string;  // 随机金额最小值
  maxAmount?: string;  // 随机金额最大值
  fixedAmount?: string; // 固定金额
  reserveAmount?: string; // 保留金额
  autoSell?: boolean; // 是否自动卖出
  sellAmountType?: string; // 卖出金额类型：ALL, RANDOM, RANDOM_PERCENT, FIXED, RESERVE
  sellMinAmount?: string; // 卖出随机金额最小值
  sellMaxAmount?: string; // 卖出随机金额最大值
  sellFixedAmount?: string; // 卖出固定金额
  sellReserveAmount?: string; // 卖出保留金额
}

// 定义队列配置接口
export interface QueueConfig {
  interval: number;    // 间隔时间(秒)
  threads: number;     // 线程数量
  taskCount: number;   // 任务数量
  runMode: QueueRunMode;  // 运行模式：串行或并行
  stopCondition: QueueStopCondition;  // 停止条件
  stopValue: number;   // 停止值 (运行次数或价格变化百分比)
}

// 定义其他配置接口
export interface OtherConfig {
  gasPrice: number;
  gasLimit: number;
}

// 定义任务配置接口
export interface TaskConfig {
  swap: SwapConfig;
  queue: QueueConfig;
  other: OtherConfig;
}

// 默认任务配置
const defaultTaskConfig: TaskConfig = {
  swap: {
    token1: null,
    token2: null,
    slippage: 0.5,
    amountType: 'ALL'
  },
  queue: {
    interval: 5,
    threads: 1,
    taskCount: 1,
    runMode: QueueRunMode.PARALLEL,
    stopCondition: QueueStopCondition.RUN_COUNT,
    stopValue: 10
  },
  other: {
    gasPrice: 5,
    gasLimit: 300000
  }
};

// 交易参数接口
export interface SwapParams {
  tokenIn: string;
  tokenOut: string;
  amountIn: string;
  tokenInDecimals: number;
  tokenOutDecimals: number;
  slippage: number;
  gasPrice?: string;
  gasLimit?: string;
  gasPriceBoost?: number;
  deadline?: number;
  maxBuyAmount?: BigNumber;
}

// 从localStorage加载初始状态
const loadInitialState = (): DexConfigState => {
  try {
    // 不再从localStorage手动加载，完全依赖Redux Persist
    return { 
      config: defaultConfig,
      task: defaultTaskConfig,
      tokens: [],
      swapParams: {
        tokenIn: '',
        tokenOut: '',
        amountIn: '',
        tokenInDecimals: 18,
        tokenOutDecimals: 18,
        slippage: 0.5
      }
    }; // 使用默认配置
  } catch (error) {
    console.error('初始化配置失败:', error);
    return { 
      config: defaultConfig,
      task: defaultTaskConfig,
      tokens: [],
      swapParams: {
        tokenIn: '',
        tokenOut: '',
        amountIn: '',
        tokenInDecimals: 18,
        tokenOutDecimals: 18,
        slippage: 0.5
      }
    };
  }
};

// 创建切片
const dexConfigSlice = createSlice({
  name: 'dexConfig',
  initialState: loadInitialState(),
  reducers: {
    // 更新整个配置
    updateConfig(state, action: PayloadAction<DexServiceConfig>) {
      state.config = action.payload;
    },
    
    // 更新钱包配置
    updateWallets(state, action: PayloadAction<WalletConfig[]>) {
      if (!state.config) {
        state.config = { ...defaultConfig };
      }
      
      // 保存旧的钱包数据中其他可能有用的字段
      const newWallets = action.payload.map((newWallet) => {
        // 查找对应的旧钱包数据
        const oldWallet = state.config.wallets.find(w => w.privateKey === newWallet.privateKey);
        
        if (oldWallet) {
          // 合并旧数据和新数据，优先使用新数据
          return {
            ...oldWallet,
            ...newWallet
          };
        }
        
        return newWallet;
      });
      
      state.config = {
        ...state.config,
        wallets: newWallets
      };
      
      // 添加日志以便调试
      console.log('钱包配置已更新到Redux:', newWallets.length, '个钱包');
    },
    
    // 更新RPC配置
    updateRpc(state, action: PayloadAction<RpcConfig>) {
      if (!state.config) {
        state.config = { ...defaultConfig };
      }
      
      state.config = {
        ...state.config,
        rpc: action.payload
      };
    },
    
    // 更新DEX配置
    updateDex(state, action: PayloadAction<DexConfig>) {
      if (!state.config) {
        state.config = { ...defaultConfig };
      }
      
      state.config = {
        ...state.config,
        dex: action.payload
      };
    },

    // 更新任务配置
    updateTaskConfig(state, action: PayloadAction<TaskConfig>) {
      state.task = action.payload;
    },

    // 更新交换配置
    updateSwapConfig(state, action: PayloadAction<SwapConfig>) {
      if (!state.task) {
        state.task = { ...defaultTaskConfig };
      }
      
      state.task = {
        ...state.task,
        swap: action.payload
      };
    },

    // 更新队列配置
    updateQueueConfig(state, action: PayloadAction<QueueConfig>) {
      if (!state.task) {
        state.task = { ...defaultTaskConfig };
      }
      
      state.task = {
        ...state.task,
        queue: action.payload
      };
    },

    // 更新其他配置
    updateOtherConfig(state, action: PayloadAction<OtherConfig>) {
      if (!state.task) {
        state.task = { ...defaultTaskConfig };
      }
      
      state.task = {
        ...state.task,
        other: action.payload
      };
    },
    
    // 更新自定义代币列表
    updateTokens(state, action: PayloadAction<TokenInfo[]>) {
      state.tokens = action.payload;
    },
    
    // 添加单个自定义代币
    addToken(state, action: PayloadAction<TokenInfo>) {
      // 检查是否已存在相同地址的代币
      const tokenExists = state.tokens.some(
        token => token.address.toLowerCase() === action.payload.address.toLowerCase()
      );
      
      // 如果不存在，则添加
      if (!tokenExists) {
        state.tokens.push(action.payload);
      }
    },
    
    // 删除单个自定义代币
    removeToken(state, action: PayloadAction<string>) {
      state.tokens = state.tokens.filter(
        token => token.address.toLowerCase() !== action.payload.toLowerCase()
      );
    },
    
    // 更新Gas设置
    updateGasSettings(state, action: PayloadAction<GasSettingsConfig>) {
      if (state.config) {
        state.config.gasSettings = action.payload;
      }
    },

    // 更新部分Gas设置
    updatePartialGasSettings(state, action: PayloadAction<Partial<GasSettingsConfig>>) {
      if (state.config && state.config.gasSettings) {
        state.config.gasSettings = {
          ...state.config.gasSettings,
          ...action.payload
        };
      } else if (state.config) {
        state.config.gasSettings = {
          mode: GasSettingMode.AUTO,
          congestionLevel: NetworkCongestionLevel.MEDIUM,
          gasPrice: '5',
          gasLimit: '300000',
          ...action.payload
        };
      }
    },

    // 更新交易参数
    updateSwapParams(state, action: PayloadAction<SwapParams>) {
      state.swapParams = action.payload;
    },

    // 更新部分交易参数
    updatePartialSwapParams(state, action: PayloadAction<Partial<SwapParams>>) {
      state.swapParams = {
        ...state.swapParams,
        ...action.payload
      };
    },
  }
});

// 导出actions
export const { 
  updateConfig, 
  updateWallets, 
  updateRpc, 
  updateDex,
  updateTaskConfig,
  updateSwapConfig,
  updateQueueConfig,
  updateOtherConfig,
  updateTokens,
  addToken,
  removeToken,
  updateSwapParams,
  updatePartialSwapParams,
  updateGasSettings,
  updatePartialGasSettings
} = dexConfigSlice.actions;

// 导出reducer
export default dexConfigSlice.reducer;

// 选择器
export const selectDexConfig = (state: RootState) => state.dexConfig.config;
export const selectWallets = (state: RootState) => state.dexConfig.config.wallets;
export const selectRpc = (state: RootState) => state.dexConfig.config.rpc;
export const selectDex = (state: RootState) => state.dexConfig.config.dex;
export const selectTaskConfig = (state: RootState) => state.dexConfig.task;
export const selectSwapConfig = (state: RootState) => state.dexConfig.task?.swap;
export const selectQueueConfig = (state: RootState) => state.dexConfig.task?.queue;
export const selectOtherConfig = (state: RootState) => state.dexConfig.task?.other;
export const selectTokens = (state: RootState) => state.dexConfig.tokens;
export const selectSwapParams = (state: RootState) => state.dexConfig.swapParams;
export const selectGasSettings = (state: RootState) => state.dexConfig.config.gasSettings;
